import axios from "axios";
import { authStore } from "@/app/store/auth.store";

const BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

export const api = axios.create({
  baseURL: BASE_URL,
  // Sends the httpOnly refresh cookie. Without this the cookie never leaves
  // the browser and every refresh 401s.
  withCredentials: true,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = authStore.get().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwrap the { success, data, meta } envelope once, here, so no component ever
// writes res.data.data.
const unwrap = (response) => {
  const body = response.data;
  if (body && typeof body === "object" && "success" in body) {
    return { ...response, data: body.data, meta: body.meta };
  }
  return response;
};

let refreshPromise = null;
let onSessionLost = () => {};
export const setSessionLostHandler = (fn) => {
  onSessionLost = fn;
};

const AUTH_FREE = ["/auth/login", "/auth/signup", "/auth/refresh", "/auth/forgot-password", "/auth/reset-password"];

api.interceptors.response.use(unwrap, async (error) => {
  const { response, config } = error;

  if (!response) {
    return Promise.reject({ code: "NETWORK_ERROR", message: "Cannot reach the server. Check your connection." });
  }

  // File downloads (responseType "blob") get their JSON error body as a
  // Blob too -- parse it so the code/message below work the same way.
  if (typeof Blob !== "undefined" && response.data instanceof Blob && /json/.test(response.data.type)) {
    try { response.data = JSON.parse(await response.data.text()); } catch { /* leave as-is */ }
  }

  const code = response.data?.error?.code;
  const isRetryable = response.status === 401 && code === "TOKEN_EXPIRED";
  const isAuthRoute = AUTH_FREE.some((p) => config.url?.includes(p));

  if (isRetryable && !config._retried && !isAuthRoute) {
    config._retried = true;

    // The mutex is the whole point. A dashboard fires eight parallel widget
    // requests; all eight 401 at once. Without deduplication that is eight
    // concurrent rotations, seven of which present a token the server has
    // already marked ROTATED -- which is exactly the signature of token theft.
    // The server then revokes the family and the user is logged out every 15
    // minutes for reasons nobody can reproduce.
    if (!refreshPromise) {
      refreshPromise = api
        .post("/auth/refresh")
        .then((res) => {
          authStore.setAccessToken(res.data.accessToken);
          return res.data.accessToken;
        })
        .catch((err) => {
          authStore.clear();
          onSessionLost();
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    try {
      const token = await refreshPromise;
      config.headers.Authorization = `Bearer ${token}`;
      return api(config);
    } catch {
      return Promise.reject({ code: "SESSION_EXPIRED", message: "Your session ended. Sign in again." });
    }
  }

  // Reuse detection or a revoked session: no retry is possible, the family is
  // already dead server-side.
  if (response.status === 401 && ["TOKEN_REUSE_DETECTED", "TOKEN_STALE", "ACCOUNT_INACTIVE"].includes(code)) {
    authStore.clear();
    onSessionLost();
  }

  return Promise.reject({
    code: code || "UNKNOWN_ERROR",
    message: response.data?.error?.message || "Something went wrong",
    details: response.data?.error?.details,
    status: response.status,
    requestId: response.data?.requestId,
  });
});

export default api;
